import { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  Image,
  Animated,
  FlatList,
  ScrollView,
  Linking,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import {
  SafeAreaProvider,
  useSafeAreaInsets,
} from "react-native-safe-area-context";
import FontAwesome from "react-native-vector-icons/FontAwesome";
import AntDesign from "react-native-vector-icons/AntDesign";
import LinearGradient from "react-native-web-linear-gradient";
import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";
import { CommonStore } from "../store/commonStore";
import { TempStore } from "../store/tempStore";
import { PRODUCT_PRICE_TYPE, UNIT_TYPE_SHORT } from "../constant/common";
import AsyncImage from "./asyncImage";
import { getPromotionSlides } from "../util/promotionLanding";
import imgLogo from "../asset/image/logo.png";
import { ReactComponent as LandingHero } from "../asset/svg/landing-hero.svg";

/**
 * LandingPageModal — redesigned KooDoo "Discover & enjoy" landing page.
 *
 * - Brand new file: legacy <ShowGreetingPopupModal /> stays untouched.
 * - Pure React Native (react-native-web) primitives: View / Text /
 *   TouchableOpacity / Image / Modal.
 * - The hero illustration or promotion carousel sits in a flexible area; short
 *   screens can scroll to the ordering actions without clipping them.
 * - Everything is sized from LIVE useWindowDimensions(), which re-renders on
 *   every resize / rotate / iOS Safari address-bar change, so the card and
 *   hero auto-track the screen size.
 * - iOS Safari bottom bar: SafeAreaProvider + useSafeAreaInsets() (same pattern
 *   the app uses in menuitemdetailsmodal) adds insets.bottom padding under the
 *   two buttons so the Safari bar can never block them.
 * - All behaviour (guest / member / close / terms consent) is identical to
 *   the legacy modal.
 */

// Design palette (darker brand green than Colors.primaryColor, per mockup).
const C = {
  pageBg: "#E9EDE6",
  cardBg: "#F7F8F5",
  topBg: "#EEF5EF",
  promoBg: "#1A3A2B",
  dark: "#1A3A2B",
  darkHover: "#22503C",
  green: "#2A5A45",
  amberWarm: "#E8B85C",
  howCardBg: "#E6F4EA",
  iconCircle: "#FFFFFF",
  title: "#111827",
  subtitle: "#6B7280",
  white: "#FFFFFF",
  link: "#2563EB",
  terms: "#4B5563",
  closeIcon: "#6B7280",
};

const PROMOTION_INTERVAL_MS = 3000;
const PROMOTION_FADE_DURATION_MS = 350;

const showConsentToast = () => {
  Toastify({
    text: "Please agree to MyKoodoo Terms of Use and Privacy Policy before continuing.",
    duration: 3000,
    newWindow: true,
    close: false,
    gravity: "top",
    position: "center",
    stopOnFocus: true,
    style: {
      background: "linear-gradient(to right, #4E9F7D, #75bd9f)",
      color: "white",
    },
    onClick: function () {},
  }).showToast();
};

const PromotionCarousel = ({
  slides,
  width,
  height,
  onSelect,
  isActive,
  immersive = false,
}) => {
  const scrollRef = useRef(null);
  const currentIndexRef = useRef(0);
  const isTransitioningRef = useRef(false);
  const fadeAnimation = useRef(new Animated.Value(1)).current;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  const showSlide = (index) => {
    if (slides.length === 0) return;
    const nextIndex = (index + slides.length) % slides.length;
    currentIndexRef.current = nextIndex;
    setCurrentIndex(nextIndex);
    scrollRef.current?.scrollTo({
      x: nextIndex * width,
      animated: false,
    });
  };

  const moveTo = (index) => {
    if (slides.length === 0 || isTransitioningRef.current) return;

    const nextIndex = (index + slides.length) % slides.length;
    if (nextIndex === currentIndexRef.current) return;

    if (reduceMotion) {
      showSlide(nextIndex);
      return;
    }

    isTransitioningRef.current = true;
    fadeAnimation.stopAnimation();
    Animated.timing(fadeAnimation, {
      toValue: 0,
      duration: PROMOTION_FADE_DURATION_MS,
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (!finished) {
        isTransitioningRef.current = false;
        return;
      }

      showSlide(nextIndex);
      Animated.timing(fadeAnimation, {
        toValue: 1,
        duration: PROMOTION_FADE_DURATION_MS,
        useNativeDriver: true,
      }).start(() => {
        isTransitioningRef.current = false;
      });
    });
  };

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = (event) => setReduceMotion(event.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    mediaQuery.addListener?.(handleChange);
    return () => mediaQuery.removeListener?.(handleChange);
  }, []);

  useEffect(() => {
    const nextIndex = slides.length
      ? Math.min(currentIndexRef.current, slides.length - 1)
      : 0;
    fadeAnimation.stopAnimation();
    fadeAnimation.setValue(1);
    isTransitioningRef.current = false;
    showSlide(nextIndex);
  }, [slides.length, width]);

  useEffect(() => () => fadeAnimation.stopAnimation(), [fadeAnimation]);

  useEffect(() => {
    if (!isActive || slides.length <= 1 || reduceMotion) {
      return undefined;
    }

    const interval = setInterval(() => {
      moveTo(currentIndexRef.current + 1);
    }, PROMOTION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isActive, reduceMotion, slides.length, width]);

  const isCompact = !immersive && height < 170;

  return (
    <View
      style={[
        styles.promotionCarousel,
        immersive && styles.promotionCarouselImmersive,
        { width, height },
      ]}
    >
      <Animated.View style={[styles.promotionSlides, { opacity: fadeAnimation }]}>
        <ScrollView
          ref={scrollRef}
          horizontal
          pagingEnabled
          decelerationRate="fast"
          showsHorizontalScrollIndicator={false}
          snapToInterval={width}
          scrollEventThrottle={16}
          onScroll={(event) => {
            const offset = event.nativeEvent.contentOffset?.x || 0;
            const nextIndex = Math.max(
              0,
              Math.min(Math.round(offset / width), slides.length - 1)
            );
            if (nextIndex !== currentIndexRef.current) {
              currentIndexRef.current = nextIndex;
              setCurrentIndex(nextIndex);
            }
          }}
          onMomentumScrollEnd={(event) => {
            const offset = event.nativeEvent.contentOffset?.x || 0;
            const nextIndex = Math.round(offset / width);
            currentIndexRef.current = nextIndex;
            setCurrentIndex(nextIndex);
          }}
        >
          {slides.map(({ promotion, items = [] }) => {
            const offer = items[0]?.offer;
            const title = promotion.campaignName || "Something special awaits";
            const description =
              promotion.campaignDescription ||
              offer?.conditions?.[0] ||
              `${items.length} ${items.length === 1 ? "item" : "items"} available`;

            return (
              <TouchableOpacity
                key={promotion.uniqueId}
                style={[styles.promotionSlide, { width, height }]}
                activeOpacity={0.94}
                accessibilityRole="button"
                accessibilityLabel={`View ${promotion.campaignName || "promotion"}`}
                onPress={() => onSelect(promotion.uniqueId)}
                testID={`landingPromotion-${promotion.uniqueId}`}
              >
                <AsyncImage
                  source={{ uri: promotion.image.trim() }}
                  item={promotion}
                  style={{ width, height }}
                />
                <LinearGradient
                  colors={[
                    "rgba(10, 14, 10, 0.05)",
                    "rgba(10, 14, 10, 0.05)",
                    "rgba(10, 14, 10, 0.55)",
                    "rgba(10, 14, 10, 0.85)",
                  ]}
                  locations={[0, 0.4, 0.78, 1]}
                  style={styles.promotionScrim}
                  pointerEvents="none"
                />
                <View
                  style={[
                    styles.promotionContent,
                    isCompact && styles.promotionContentCompact,
                    immersive && styles.promotionContentImmersive,
                  ]}
                  pointerEvents="none"
                >
                  <Text
                    style={[
                      styles.promotionEyebrow,
                      immersive && styles.promotionEyebrowImmersive,
                    ]}
                    numberOfLines={1}
                  >
                    {offer?.label || "Special offer"}
                  </Text>
                  <Text
                    style={[
                      styles.promotionTitle,
                      immersive && styles.promotionTitleImmersive,
                    ]}
                    numberOfLines={isCompact ? 1 : 2}
                  >
                    {title}
                  </Text>
                  {!isCompact ? (
                    <Text
                      style={[
                        styles.promotionCaption,
                        immersive && styles.promotionCaptionImmersive,
                      ]}
                      numberOfLines={immersive ? 2 : 1}
                    >
                      {description}
                    </Text>
                  ) : null}
                  <View
                    style={[
                      styles.promotionViewButton,
                      isCompact && styles.promotionViewButtonCompact,
                      immersive && styles.promotionViewButtonImmersive,
                    ]}
                  >
                    <Text style={styles.promotionViewButtonText}>View more</Text>
                    <AntDesign
                      name="arrowright"
                      size={immersive ? 12 : 14}
                      color={C.white}
                    />
                  </View>
                </View>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </Animated.View>

      {slides.length > 1 ? (
        <>
          <TouchableOpacity
            style={[styles.promotionArrow, styles.promotionArrowLeft]}
            onPress={() => moveTo(currentIndexRef.current - 1)}
            accessibilityRole="button"
            accessibilityLabel="Previous promotion"
            testID="previousPromotion"
          >
            <View style={styles.promotionArrowVisual}>
              <AntDesign name="left" size={13} color={C.white} />
            </View>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.promotionArrow, styles.promotionArrowRight]}
            onPress={() => moveTo(currentIndexRef.current + 1)}
            accessibilityRole="button"
            accessibilityLabel="Next promotion"
            testID="nextPromotion"
          >
            <View style={styles.promotionArrowVisual}>
              <AntDesign name="right" size={13} color={C.white} />
            </View>
          </TouchableOpacity>
          <View
            style={[
              styles.promotionDots,
              isCompact ? styles.promotionDotsTop : styles.promotionDotsBottom,
            ]}
            pointerEvents="none"
          >
            {slides.map(({ promotion }, index) => (
              <View
                key={promotion.uniqueId}
                style={styles.promotionDotTarget}
              >
                <View
                  style={[
                    styles.promotionDot,
                    index === currentIndex && styles.promotionDotActive,
                  ]}
                />
              </View>
            ))}
          </View>
        </>
      ) : null}
    </View>
  );
};

const PromotionItemsSheet = ({ slide, isMobile, onClose, onSelectItem }) => {
  const sheetRef = useRef(null);
  const closeButtonRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const promotionId = slide?.promotion?.uniqueId;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!promotionId || typeof document === "undefined") return undefined;

    const focusTimer = setTimeout(() => closeButtonRef.current?.focus?.(), 0);
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !sheetRef.current?.querySelectorAll) return;

      const focusableElements = Array.from(
        sheetRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.disabled);
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [promotionId]);

  if (!slide) return null;

  const { promotion, items } = slide;

  return (
    <View
      style={styles.promotionBackdrop}
      accessibilityRole="dialog"
      accessibilityModal={true}
      accessibilityLabel={promotion.campaignName || "Promotion items"}
    >
      <View
        ref={sheetRef}
        style={[
          styles.promotionSheet,
          isMobile ? styles.promotionSheetMobile : styles.promotionSheetDesktop,
        ]}
      >
        <View style={styles.promotionSheetHeader}>
          <View style={styles.promotionSheetHeading}>
            <Text style={styles.promotionSheetEyebrow}>PROMOTION</Text>
            <Text style={styles.promotionSheetTitle} numberOfLines={2}>
              {promotion.campaignName || "Special offer"}
            </Text>
            {promotion.campaignDescription ? (
              <Text style={styles.promotionSheetDescription}>
                {promotion.campaignDescription}
              </Text>
            ) : null}
            <Text style={styles.promotionItemCount}>
              {items.length} {items.length === 1 ? "item" : "items"} available
            </Text>
          </View>
          <TouchableOpacity
            ref={closeButtonRef}
            style={styles.promotionSheetClose}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close promotion items"
            testID="closePromotionItems"
          >
            <AntDesign name="close" size={20} color={C.closeIcon} />
          </TouchableOpacity>
        </View>

        <FlatList
          style={styles.promotionItemListContainer}
          data={items}
          keyExtractor={({ item }) => item.uniqueId || item.sku}
          initialNumToRender={8}
          windowSize={7}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.promotionItemList}
          renderItem={({ item: promotionItem }) => {
            const { item, category, offer } = promotionItem;
            const itemName = item.dpName || item.name || "Menu item";
            const unitSuffix =
              item.priceType === PRODUCT_PRICE_TYPE.UNIT &&
              UNIT_TYPE_SHORT[item.unitType]
                ? `/${UNIT_TYPE_SHORT[item.unitType]}`
                : "";
            const accessibilityPrice =
              offer.discountedPrice !== null
                ? `Regular price RM${offer.originalPrice.toFixed(2)}. ${
                    offer.isConditional ? "Price from" : "Discounted price"
                  } RM${offer.discountedPrice.toFixed(2)}.`
                : `Price RM${offer.originalPrice.toFixed(2)}.`;
            const accessibilityConditions = offer.conditions.length
              ? ` ${offer.conditions.join(". ")}.`
              : "";

            return (
              <TouchableOpacity
                style={styles.promotionItemCard}
                activeOpacity={0.82}
                onPress={() => onSelectItem(item)}
                accessibilityRole="button"
                accessibilityLabel={`${itemName}. ${offer.label}. ${accessibilityPrice}${accessibilityConditions} View details.`}
                testID={`promotionItem-${item.uniqueId}`}
              >
                <View style={styles.promotionItemImage}>
                  {item.image ? (
                    <AsyncImage
                      source={{ uri: item.image }}
                      item={item}
                      style={{ width: 82, height: 82, borderRadius: 14 }}
                    />
                  ) : (
                    <FontAwesome name="cutlery" size={25} color={C.green} />
                  )}
                </View>

                <View style={styles.promotionItemBody}>
                  <Text style={styles.promotionItemName} numberOfLines={2}>
                    {itemName}
                  </Text>
                  {category?.name ? (
                    <Text style={styles.promotionItemCategory} numberOfLines={1}>
                      {category.name}
                    </Text>
                  ) : null}
                  <View style={styles.promotionOfferBadge}>
                    <Text style={styles.promotionOfferText}>
                      {offer.label}
                    </Text>
                  </View>
                  <View style={styles.promotionPriceRow}>
                    <Text
                      style={[
                        styles.promotionOriginalPrice,
                        offer.discountedPrice !== null && styles.promotionPriceStruck,
                      ]}
                    >
                      RM{offer.originalPrice.toFixed(2)}{unitSuffix}
                    </Text>
                    {offer.discountedPrice !== null ? (
                      <Text style={styles.promotionDiscountedPrice}>
                        {offer.isConditional ? "From " : ""}RM
                        {offer.discountedPrice.toFixed(2)}{unitSuffix}
                      </Text>
                    ) : null}
                  </View>
                  {offer.conditions.length > 0 ? (
                    <Text style={styles.promotionConditions}>
                      {offer.conditions.join(" | ")}
                    </Text>
                  ) : null}
                </View>

                <AntDesign name="right" size={18} color={C.green} />
              </TouchableOpacity>
            );
          }}
        />
      </View>
    </View>
  );
};

const LandingPageContent = () => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();

  const showGreetingPopup = TempStore.useState((s) => s.showGreetingPopup);
  const selectedOutlet = CommonStore.useState((s) => s.selectedOutlet);
  const tickboxConsent = TempStore.useState((s) => s.tickboxConsent);
  const showStartAsGuestButton = TempStore.useState(
    (s) => s.showStartAsGuestButton
  );
  const availablePromotions = CommonStore.useState(
    (s) => s.availablePromotions
  );
  const selectedOutletItems = CommonStore.useState(
    (s) => s.selectedOutletItems
  );
  const selectedOutletItemCategoriesDict = CommonStore.useState(
    (s) => s.selectedOutletItemCategoriesDict
  );
  const selectedOutletSectionId = CommonStore.useState(
    (s) => s.selectedOutletSectionId
  );
  const orderType = CommonStore.useState((s) => s.orderType);
  const isSwitchingOutlets = CommonStore.useState(
    (s) => s.isSwitchingOutlets
  );
  const [selectedPromotionId, setSelectedPromotionId] = useState(null);
  const promotionTriggerFocusRef = useRef(null);

  const openPromotionItems = (promotionId) => {
    if (typeof document !== "undefined") {
      promotionTriggerFocusRef.current = document.activeElement;
    }
    setSelectedPromotionId(promotionId);
  };

  const closePromotionItems = () => {
    setSelectedPromotionId(null);
    setTimeout(() => promotionTriggerFocusRef.current?.focus?.(), 0);
  };

  const promotionSlides = useMemo(() => {
    if (
      isSwitchingOutlets ||
      !selectedOutlet?.uniqueId ||
      selectedOutletItems.length === 0
    ) {
      return [];
    }

    return getPromotionSlides({
      promotions: availablePromotions,
      items: selectedOutletItems,
      categoriesById: selectedOutletItemCategoriesDict,
      selectedOutlet,
      orderType,
      outletSectionId: selectedOutletSectionId,
    });
  }, [
    availablePromotions,
    isSwitchingOutlets,
    orderType,
    selectedOutlet,
    selectedOutletItemCategoriesDict,
    selectedOutletItems,
    selectedOutletSectionId,
  ]);

  const selectedPromotionSlide = promotionSlides.find(
    ({ promotion }) => promotion.uniqueId === selectedPromotionId
  );

  useEffect(() => {
    if (selectedPromotionId && !selectedPromotionSlide) {
      setSelectedPromotionId(null);
      setTimeout(() => promotionTriggerFocusRef.current?.focus?.(), 0);
    }
  }, [selectedPromotionId, selectedPromotionSlide]);

  if (!showGreetingPopup) {
    return null;
  }

  // Live dimensions — useWindowDimensions re-renders on every resize / rotate /
  // iOS Safari address-bar change, so the card and its hero auto-track.
  const isMobile = windowWidth <= 480;
  const cardWidth = isMobile ? windowWidth : Math.min(windowWidth * 0.9, 420);
  const cardHeight = isMobile
    ? windowHeight
    : Math.min(windowHeight * 0.92, 780);

  const showTerms =
    selectedOutlet && selectedOutlet.tickTerms && showStartAsGuestButton;
  const hasPromotionSlides = promotionSlides.length > 0;
  const promotionBannerHeight = Math.max(340, Math.round(cardHeight * 0.8));

  const reserved =
    30 + // logo row
    84 + // headline
    200 + // how-to-order card
    160 + // buttons
    (insets.bottom || 0) +
    (showTerms ? 44 : 0) +
    30; // assorted paddings
  const heroH = Math.max(120, Math.min(cardHeight - reserved, 210));
  const heroW = Math.min(heroH * (320 / 270), 260, cardWidth - 48);

  const guardConsent = () => {
    if (tickboxConsent !== true && showTerms) {
      showConsentToast();
      return false;
    }
    return true;
  };

  const setPendingPromotionItem = (itemId, promotionId) => {
    TempStore.update((s) => {
      s.pendingGreetingPromotionItemId = itemId || null;
      s.pendingGreetingPromotionId = promotionId || null;
    });
  };

  const continueAsGuest = (itemId = null, promotionId = null) => {
    setPendingPromotionItem(itemId, promotionId);
    TempStore.update((s) => {
      s.showGreetingPopup = false;
    });
    CommonStore.update((s) => {
      s.currPageIframe = "OutletMenu";
    });
    setTimeout(() => {
      if (global.toShowSignUpMemberModal) {
        TempStore.update((s) => {
          s.showSignUpMember = true;
        });
      }
    }, 3000);
    TempStore.update((s) => {
      s.pendingPayFirstReminderCheck = "guest";
    });
  };

  const continueAsMember = (itemId = null, promotionId = null) => {
    setPendingPromotionItem(itemId, promotionId);
    TempStore.update((s) => {
      s.showGreetingPopup = false;
      s.startAsMember = true;
    });
    CommonStore.update((s) => {
      s.currPageIframe = "OutletMenu";
    });
    TempStore.update((s) => {
      s.pendingPayFirstReminderCheck = "member";
    });
  };

  // X close is treated as the guest flow, same as the legacy landing page.
  const handleClose = () => {
    continueAsGuest();
  };

  const handleGuest = () => {
    if (!guardConsent()) return;
    continueAsGuest();
  };

  const handleMember = () => {
    if (!guardConsent()) return;
    continueAsMember();
  };

  const handlePromotionItem = (item) => {
    if (!item?.uniqueId || !selectedPromotionId || !guardConsent()) return;

    if (showStartAsGuestButton) {
      continueAsGuest(item.uniqueId, selectedPromotionId);
    } else {
      continueAsMember(item.uniqueId, selectedPromotionId);
    }
  };

  const toggleConsent = () => {
    TempStore.update((s) => {
      s.tickboxConsent = tickboxConsent !== true;
    });
  };

  const renderOrderActions = (isPromotionLayout = false) => (
    <>
      {showStartAsGuestButton ? (
        <TouchableOpacity
          style={[styles.btn, styles.btnPrimary]}
          onPress={handleGuest}
          testID="orderAsGuest"
        >
          <FontAwesome name="cutlery" size={17} color={C.white} />
          <Text style={[styles.btnText, styles.btnTextPrimary]}>
            Order as Guest
          </Text>
        </TouchableOpacity>
      ) : null}

      <TouchableOpacity
        style={[
          styles.btn,
          styles.btnSecondary,
          isPromotionLayout && styles.promoBtnSecondary,
          !showStartAsGuestButton && styles.btnSecondaryOnly,
        ]}
        onPress={handleMember}
        testID="signInAsMember"
      >
        <FontAwesome name="user-plus" size={16} color={C.dark} />
        <Text style={[styles.btnText, styles.btnTextSecondary]}>
          Sign in as Member
        </Text>
      </TouchableOpacity>
    </>
  );

  const renderTerms = (isPromotionLayout = false) => {
    if (!showTerms) return null;

    return (
      <View
        style={isPromotionLayout ? styles.promoTermsRow : styles.termsRow}
      >
        <TouchableOpacity
          style={isPromotionLayout ? styles.promoCheckbox : styles.checkbox}
          onPress={toggleConsent}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: tickboxConsent === true }}
          accessibilityLabel="Agree to MyKoodoo terms and privacy policy"
          testID="consentCheckbox"
        >
          {tickboxConsent === true ? (
            <AntDesign name="checksquare" size={20} color={C.dark} />
          ) : (
            <AntDesign name="checksquareo" size={20} color={C.terms} />
          )}
        </TouchableOpacity>
        <Text
          style={isPromotionLayout ? styles.promoTermsText : styles.termsText}
        >
          {"By continuing you agree to MyKoodoo "}
          <Text
            style={styles.link}
            onPress={() => Linking.openURL("https://mykoodoo.com/terms/")}
          >
            Terms of Use
          </Text>
          {" & "}
          <Text
            style={styles.link}
            onPress={() =>
              Linking.openURL("https://mykoodoo.com/privacy-policy/")
            }
          >
            Privacy Policy
          </Text>
        </Text>
      </View>
    );
  };

  return (
    <View
      style={[
        styles.overlay,
        hasPromotionSlides && styles.overlayPromotion,
        { width: windowWidth, height: windowHeight },
      ]}
    >
      <View
        style={[
          styles.card,
          hasPromotionSlides && styles.cardPromotion,
          {
            width: cardWidth,
            height: cardHeight,
            borderRadius: isMobile ? 0 : 32,
          },
        ]}
      >
        <ScrollView
          style={styles.landingScroll}
          contentContainerStyle={styles.landingScrollContent}
          bounces={false}
          showsVerticalScrollIndicator={false}
        >
          {hasPromotionSlides ? (
            <>
              <View
                style={[styles.promoBanner, { height: promotionBannerHeight }]}
              >
                <PromotionCarousel
                  slides={promotionSlides}
                  width={cardWidth}
                  height={promotionBannerHeight}
                  onSelect={openPromotionItems}
                  isActive={!selectedPromotionSlide}
                  immersive={true}
                />
                <View
                  style={[
                    styles.promoHeader,
                    { paddingTop: 20 + (isMobile ? insets.top || 0 : 0) },
                  ]}
                  pointerEvents="box-none"
                >
                  <Image
                    style={styles.promoLogo}
                    resizeMode="contain"
                    source={imgLogo}
                    accessibilityRole="image"
                    accessibilityLabel="KooDoo"
                  />
                  <TouchableOpacity
                    style={[
                      styles.promoClose,
                      { top: 20 + (isMobile ? insets.top || 0 : 0) },
                    ]}
                    onPress={handleClose}
                    accessibilityRole="button"
                    accessibilityLabel="Close landing page"
                    testID="closeButton"
                  >
                    <AntDesign name="close" size={18} color={C.white} />
                  </TouchableOpacity>
                </View>
              </View>

              <View
                style={[
                  styles.promoBody,
                  { paddingBottom: 20 + (insets.bottom || 0) },
                ]}
              >
                <View style={styles.promoButtons}>{renderOrderActions(true)}</View>
                {renderTerms(true)}
              </View>
            </>
          ) : (
            <>
              <View style={styles.top}>
                <View style={styles.logoRow}>
                  <Image
                    style={styles.logo}
                    resizeMode="contain"
                    source={imgLogo}
                    accessibilityRole="image"
                    accessibilityLabel="KooDoo"
                  />
                  <TouchableOpacity
                    style={styles.close}
                    onPress={handleClose}
                    accessibilityRole="button"
                    accessibilityLabel="Close landing page"
                    testID="closeButton"
                  >
                    <AntDesign name="close" size={16} color={C.closeIcon} />
                  </TouchableOpacity>
                </View>

                <View style={styles.heroWrap}>
                  <LandingHero
                    width={heroW}
                    height={heroH}
                    preserveAspectRatio="xMidYMid meet"
                  />
                </View>
              </View>

              <View style={styles.headline}>
                <Text style={styles.h1}>Discover & enjoy</Text>
                <Text style={styles.lead}>
                  Order your favourite meals in just a few taps.
                </Text>
              </View>

              <View style={styles.howCard}>
                <Text style={styles.howTitle}>How to Order</Text>
                <Text style={styles.howSubtitle}>
                  Ordering food has never been easier
                </Text>

                <View style={styles.steps}>
                  <View style={styles.step}>
                    <View style={styles.stepIcon}>
                      <FontAwesome name="shopping-cart" size={24} color={C.dark} />
                    </View>
                    <Text style={styles.stepLabel}>Add to cart</Text>
                  </View>
                  <View style={styles.step}>
                    <View style={styles.stepIcon}>
                      <FontAwesome name="sign-in" size={24} color={C.dark} />
                    </View>
                    <Text style={styles.stepLabel}>Quick sign-in</Text>
                  </View>
                  <View style={styles.step}>
                    <View style={styles.stepIcon}>
                      <FontAwesome name="file-text-o" size={23} color={C.dark} />
                    </View>
                    <Text style={styles.stepLabel}>Place order</Text>
                  </View>
                </View>
              </View>

              {renderTerms()}

              <View
                style={[
                  styles.buttons,
                  { paddingBottom: 18 + (insets.bottom || 0) },
                ]}
              >
                {renderOrderActions()}
              </View>
            </>
          )}
        </ScrollView>

        <PromotionItemsSheet
          slide={selectedPromotionSlide}
          isMobile={isMobile}
          onClose={closePromotionItems}
          onSelectItem={handlePromotionItem}
        />
      </View>
    </View>
  );
};

const LandingPageModal = () => {
  const showGreetingPopup = TempStore.useState((s) => s.showGreetingPopup);

  if (!showGreetingPopup) {
    return null;
  }

  return (
    <SafeAreaProvider>
      <Modal visible={true} transparent={true} animationType="fade">
        <LandingPageContent />
      </Modal>
    </SafeAreaProvider>
  );
};

const styles = StyleSheet.create({
  overlay: {
    backgroundColor: C.pageBg,
    alignItems: "center",
    justifyContent: "center",
  },
  overlayPromotion: {
    backgroundColor: "#E4E8E0",
  },
  card: {
    backgroundColor: C.cardBg,
    overflow: "hidden",
    flexDirection: "column",
    shadowColor: "#1A3A2B",
    shadowOffset: { width: 0, height: 18 },
    shadowOpacity: 0.18,
    shadowRadius: 40,
    elevation: 8,
  },
  cardPromotion: {
    backgroundColor: C.white,
  },
  landingScroll: {
    flex: 1,
  },
  landingScrollContent: {
    flexGrow: 1,
  },

  promoBanner: {
    position: "relative",
    overflow: "hidden",
    backgroundColor: C.promoBg,
  },
  promoHeader: {
    position: "absolute",
    top: 0,
    right: 0,
    left: 0,
    zIndex: 10,
    paddingHorizontal: 22,
    paddingBottom: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  promoLogo: {
    width: 142,
    height: 28,
  },
  promoClose: {
    position: "absolute",
    right: 22,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "rgba(10, 14, 10, 0.42)",
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.34)",
    alignItems: "center",
    justifyContent: "center",
  },
  promoBody: {
    backgroundColor: C.white,
    paddingTop: 22,
    paddingHorizontal: 20,
  },
  promoButtons: {
    width: "100%",
  },

  top: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 24,
    paddingTop: 24,
    backgroundColor: C.topBg,
  },
  logoRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  logo: { height: 30, width: 150 },
  close: {
    position: "absolute",
    right: 0,
    top: -2,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: C.white,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#111827",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.16,
    shadowRadius: 6,
    elevation: 2,
  },
  heroWrap: {
    flex: 1,
    minHeight: 0,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 6,
    overflow: "hidden",
  },
  promotionCarousel: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 18,
    backgroundColor: C.promoBg,
  },
  promotionCarouselImmersive: {
    borderRadius: 0,
  },
  promotionSlides: {
    flex: 1,
  },
  promotionSlide: {
    overflow: "hidden",
    backgroundColor: C.promoBg,
  },
  promotionScrim: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  promotionContent: {
    position: "absolute",
    right: 0,
    bottom: 30,
    left: 0,
    paddingHorizontal: 18,
  },
  promotionContentCompact: {
    bottom: 12,
  },
  promotionContentImmersive: {
    bottom: 42,
    paddingHorizontal: 26,
  },
  promotionEyebrow: {
    color: C.amberWarm,
    fontFamily: "NunitoSans-Bold",
    fontSize: 10,
    letterSpacing: 1.8,
    textTransform: "uppercase",
    marginBottom: 5,
  },
  promotionEyebrowImmersive: {
    fontSize: 10.5,
    letterSpacing: 2.5,
    marginBottom: 8,
  },
  promotionTitle: {
    color: C.white,
    fontFamily: "NunitoSans-Bold",
    fontSize: 22,
    lineHeight: 26,
    letterSpacing: -0.3,
  },
  promotionTitleImmersive: {
    fontSize: 27,
    lineHeight: 32,
  },
  promotionCaption: {
    marginTop: 5,
    color: "rgba(255, 255, 255, 0.78)",
    fontFamily: "NunitoSans-Regular",
    fontSize: 11.5,
    lineHeight: 17,
  },
  promotionCaptionImmersive: {
    maxWidth: 260,
    marginTop: 8,
    fontSize: 12.5,
    lineHeight: 19,
  },
  promotionViewButton: {
    alignSelf: "flex-start",
    minHeight: 36,
    marginTop: 10,
    paddingHorizontal: 14,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.45)",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    flexDirection: "row",
    alignItems: "center",
  },
  promotionViewButtonCompact: {
    minHeight: 32,
    marginTop: 8,
    paddingHorizontal: 12,
  },
  promotionViewButtonImmersive: {
    minHeight: 42,
    marginTop: 18,
    paddingLeft: 20,
    paddingRight: 18,
    borderRadius: 26,
  },
  promotionViewButtonText: {
    marginRight: 7,
    color: C.white,
    fontFamily: "NunitoSans-Bold",
    fontSize: 11,
    letterSpacing: 1.2,
    textTransform: "uppercase",
  },
  promotionArrow: {
    position: "absolute",
    top: "50%",
    zIndex: 5,
    width: 44,
    height: 44,
    marginTop: -22,
    alignItems: "center",
    justifyContent: "center",
  },
  promotionArrowLeft: {
    left: 6,
  },
  promotionArrowRight: {
    right: 6,
  },
  promotionArrowVisual: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.24)",
    backgroundColor: "rgba(255, 255, 255, 0.14)",
    alignItems: "center",
    justifyContent: "center",
  },
  promotionDots: {
    position: "absolute",
    zIndex: 4,
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
  promotionDotsTop: {
    top: 4,
  },
  promotionDotsBottom: {
    bottom: 8,
  },
  promotionDotTarget: {
    width: 22,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  promotionDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: "rgba(255, 255, 255, 0.35)",
  },
  promotionDotActive: {
    width: 16,
    backgroundColor: C.amberWarm,
  },
  headline: { paddingHorizontal: 28, paddingTop: 6, alignItems: "center" },
  h1: {
    fontFamily: "NunitoSans-Bold",
    fontSize: 27,
    color: C.title,
    textAlign: "center",
  },
  lead: {
    fontFamily: "NunitoSans-SemiBold",
    fontSize: 14,
    lineHeight: 21,
    color: C.subtitle,
    textAlign: "center",
    marginTop: 8,
    maxWidth: 250,
  },
  howCard: {
    marginHorizontal: 20,
    marginTop: 16,
    backgroundColor: C.howCardBg,
    borderRadius: 24,
    paddingHorizontal: 18,
    paddingTop: 18,
    paddingBottom: 18,
    shadowColor: "#1A3A2B",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 18,
    elevation: 3,
  },
  howTitle: {
    fontFamily: "NunitoSans-Bold",
    fontSize: 18,
    color: C.dark,
    textAlign: "center",
  },
  howSubtitle: {
    fontFamily: "NunitoSans-SemiBold",
    fontSize: 12.5,
    color: C.green,
    textAlign: "center",
    marginTop: 4,
    opacity: 0.9,
  },
  steps: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginTop: 18,
  },
  step: {
    alignItems: "center",
    flex: 1,
  },
  stepIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: C.iconCircle,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#1A3A2B",
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.18,
    shadowRadius: 10,
    elevation: 2,
  },
  stepLabel: {
    fontFamily: "NunitoSans-SemiBold",
    fontSize: 12,
    color: C.dark,
    textAlign: "center",
    marginTop: 10,
  },
  termsRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginHorizontal: 24,
    marginTop: 14,
  },
  checkbox: {
    marginRight: 8,
    marginTop: 1,
  },
  termsText: {
    flex: 1,
    fontFamily: "NunitoSans-Regular",
    fontSize: 12,
    lineHeight: 18,
    color: C.terms,
  },
  promoTermsRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 14,
  },
  promoCheckbox: {
    width: 44,
    height: 44,
    marginLeft: -8,
    marginRight: -2,
    alignItems: "center",
    justifyContent: "center",
  },
  promoTermsText: {
    flex: 1,
    fontFamily: "NunitoSans-Regular",
    fontSize: 11,
    lineHeight: 17,
    color: C.terms,
    textAlign: "center",
  },
  link: {
    fontFamily: "NunitoSans-SemiBold",
    color: C.link,
    textDecorationLine: "underline",
  },

  buttons: {
    paddingHorizontal: 20,
    paddingTop: 16,
    backgroundColor: C.cardBg,
  },
  btn: {
    width: "100%",
    borderRadius: 100,
    paddingVertical: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  btnPrimary: {
    backgroundColor: C.dark,
    shadowColor: "#1A3A2B",
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.45,
    shadowRadius: 20,
    elevation: 5,
  },
  btnSecondary: {
    backgroundColor: C.white,
    borderWidth: 2,
    borderColor: C.dark,
    marginTop: 12,
  },
  promoBtnSecondary: {
    borderWidth: 1.5,
    marginTop: 10,
  },
  btnSecondaryOnly: {
    marginTop: 0,
  },
  btnText: {
    fontFamily: "NunitoSans-Bold",
    fontSize: 15,
    marginLeft: 8,
  },
  btnTextPrimary: { color: C.white },
  btnTextSecondary: { color: C.dark },

  promotionBackdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 100,
    elevation: 20,
    backgroundColor: "rgba(17, 24, 39, 0.58)",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  promotionSheet: {
    backgroundColor: C.cardBg,
    overflow: "hidden",
    shadowColor: "#111827",
    shadowOffset: { width: 0, height: -8 },
    shadowOpacity: 0.22,
    shadowRadius: 24,
    elevation: 22,
  },
  promotionSheetMobile: {
    width: "100%",
    height: "91%",
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
  },
  promotionSheetDesktop: {
    width: "92%",
    height: "88%",
    borderRadius: 24,
    marginVertical: "auto",
  },
  promotionSheetHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingTop: 22,
    paddingBottom: 18,
    paddingLeft: 22,
    paddingRight: 14,
    borderBottomWidth: 1,
    borderBottomColor: "#DCE6DE",
    backgroundColor: C.topBg,
  },
  promotionSheetHeading: {
    flex: 1,
    paddingRight: 10,
  },
  promotionSheetEyebrow: {
    color: C.green,
    fontFamily: "NunitoSans-Bold",
    fontSize: 10,
    letterSpacing: 1.4,
  },
  promotionSheetTitle: {
    marginTop: 4,
    color: C.title,
    fontFamily: "NunitoSans-Bold",
    fontSize: 22,
    lineHeight: 27,
  },
  promotionSheetDescription: {
    marginTop: 6,
    color: C.subtitle,
    fontFamily: "NunitoSans-Regular",
    fontSize: 13,
    lineHeight: 18,
  },
  promotionItemCount: {
    marginTop: 8,
    color: C.green,
    fontFamily: "NunitoSans-SemiBold",
    fontSize: 12,
  },
  promotionSheetClose: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: C.white,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#111827",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.14,
    shadowRadius: 6,
    elevation: 2,
  },
  promotionItemList: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 28,
  },
  promotionItemListContainer: {
    flex: 1,
  },
  promotionItemCard: {
    minHeight: 108,
    marginBottom: 12,
    padding: 12,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "#E0E8E2",
    backgroundColor: C.white,
    flexDirection: "row",
    alignItems: "center",
    shadowColor: "#1A3A2B",
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 2,
  },
  promotionItemImage: {
    width: 82,
    height: 82,
    borderRadius: 14,
    overflow: "hidden",
    backgroundColor: C.howCardBg,
    alignItems: "center",
    justifyContent: "center",
  },
  promotionItemBody: {
    flex: 1,
    minWidth: 0,
    marginHorizontal: 12,
  },
  promotionItemName: {
    color: C.title,
    fontFamily: "NunitoSans-Bold",
    fontSize: 15,
    lineHeight: 19,
  },
  promotionItemCategory: {
    marginTop: 2,
    color: C.subtitle,
    fontFamily: "NunitoSans-Regular",
    fontSize: 11,
  },
  promotionOfferBadge: {
    alignSelf: "flex-start",
    maxWidth: "100%",
    marginTop: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: C.howCardBg,
  },
  promotionOfferText: {
    color: C.dark,
    fontFamily: "NunitoSans-Bold",
    fontSize: 11,
    lineHeight: 14,
  },
  promotionPriceRow: {
    marginTop: 6,
    flexDirection: "row",
    alignItems: "baseline",
    flexWrap: "wrap",
  },
  promotionOriginalPrice: {
    color: C.green,
    fontFamily: "NunitoSans-Bold",
    fontSize: 13,
  },
  promotionPriceStruck: {
    color: C.subtitle,
    textDecorationLine: "line-through",
  },
  promotionDiscountedPrice: {
    marginLeft: 7,
    color: "#B45309",
    fontFamily: "NunitoSans-Bold",
    fontSize: 14,
  },
  promotionConditions: {
    marginTop: 4,
    color: C.subtitle,
    fontFamily: "NunitoSans-Regular",
    fontSize: 10,
    lineHeight: 14,
  },
});

export default LandingPageModal;
